import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Clock, Users, Heart, Share2, Eye, MessageSquare } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface SOSReport {
  id: string;
  user_id: string;
  type: 'lost' | 'found' | 'emergency' | 'danger';
  title: string;
  description: string | null;
  animal_species: string | null;
  animal_breed: string | null;
  animal_name: string | null;
  image_url: string | null;
  latitude: number;
  longitude: number;
  location_precision: 'precise' | 'street' | 'approximate';
  status: 'open' | 'spotted' | 'in_progress' | 'resolved' | 'cancelled';
  contact_phone: string | null;
  allow_public_sharing: boolean;
  last_spotted_at: string | null;
  helper_count: number;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface SOSListProps {
  reports: SOSReport[];
  loading: boolean;
  onRefresh: () => void;
  userLocation: { lat: number; lng: number } | null;
  onView: (report: SOSReport) => void;
}

export const SOSList = ({ reports, loading, onRefresh, userLocation, onView }: SOSListProps) => {
  const { user } = useAuth();
  const [joiningHelper, setJoiningHelper] = useState<string | null>(null);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'lost': return '🐕';
      case 'found': return '🔍';
      case 'emergency': return '🚨';
      case 'danger': return '⚠️';
      default: return '📍';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lost': return 'Smarrito';
      case 'found': return 'Trovato';
      case 'emergency': return 'Emergenza';
      case 'danger': return 'Pericolo';
      default: return 'Generale';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'destructive';
      case 'spotted': return 'secondary';
      case 'in_progress': return 'default';
      case 'resolved': return 'outline';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Aperto';
      case 'spotted': return 'Avvistato';
      case 'in_progress': return 'In corso';
      case 'resolved': return 'Risolto';
      case 'cancelled': return 'Annullato';
      default: return status;
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Radius of Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getDistance = (report: SOSReport) => {
    if (!userLocation) return null;
    const distance = calculateDistance(
      userLocation.lat, 
      userLocation.lng, 
      report.latitude, 
      report.longitude
    );
    
    if (distance < 1) {
      return `${(distance * 1000).toFixed(0)}m`;
    }
    return `${distance.toFixed(1)}km`;
  };

  const handleJoinHelp = async (reportId: string) => {
    if (!user) {
      toast({
        title: "Accesso richiesto",
        description: "Devi essere autenticato per unirti all'aiuto",
        variant: "destructive"
      });
      return;
    }

    try {
      setJoiningHelper(reportId);
      
      const { error } = await supabase
        .from('sos_helpers')
        .insert({
          sos_report_id: reportId,
          user_id: user.id
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "Già iscritto",
            description: "Ti sei già unito a questo SOS",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Grazie per l'aiuto!",
          description: "Ti sei unito al SOS. Riceverai aggiornamenti sul caso.",
        });
        onRefresh();
      }
    } catch (error) {
      console.error('Error joining help:', error);
      toast({
        title: "Errore",
        description: "Impossibile unirti all'aiuto. Riprova.",
        variant: "destructive"
      });
    } finally {
      setJoiningHelper(null);
    }
  };

  const handleShare = async (report: SOSReport) => {
    const link = `${window.location.origin}/app/sos?sosId=${report.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast({
        title: "Link copiato",
        description: "Link diretto alla lista SOS copiato negli appunti",
      });
    } catch (e) {
      console.error('Clipboard error:', e);
      toast({
        title: "Errore",
        description: "Impossibile copiare il link",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <Skeleton className="w-16 h-16 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <Card className="text-center py-8">
        <CardContent>
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-semibold mb-2">Nessun SOS trovato</h3>
          <p className="text-muted-foreground mb-4">
            Non ci sono SOS attivi nella tua zona al momento
          </p>
          <Button onClick={onRefresh} variant="outline">
            Aggiorna
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {reports.map((report) => (
        <Card key={report.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex gap-4">
              {/* Image or icon */}
              <div className="flex-shrink-0">
                {report.image_url ? (
                  <img 
                    src={report.image_url} 
                    alt={report.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-2xl">
                    {getTypeIcon(report.type)}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={getStatusColor(report.status)}>
                        {getStatusLabel(report.status)}
                      </Badge>
                      <Badge variant="outline">
                        {getTypeLabel(report.type)}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-sm truncate">{report.title}</h3>
                  </div>
                  {getDistance(report) && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {getDistance(report)}
                    </div>
                  )}
                </div>

                {/* Animal info */}
                {report.animal_species && (
                  <div className="text-sm text-muted-foreground mb-2">
                    🐾 {report.animal_species}
                    {report.animal_breed && ` • ${report.animal_breed}`}
                    {report.animal_name && ` • ${report.animal_name}`}
                  </div>
                )}

                {/* Description */}
                {report.description && (
                  <p className="text-sm line-clamp-2 mb-3">{report.description}</p>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(new Date(report.created_at), { 
                        addSuffix: true, 
                        locale: it 
                      })}
                    </div>
                    {report.helper_count > 0 && (
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {report.helper_count} aiutano
                      </div>
                    )}
                    {report.profiles && (
                      <div className="flex items-center gap-1">
                        <Avatar className="w-4 h-4">
                          <AvatarImage src={report.profiles.avatar_url || ''} />
                          <AvatarFallback className="text-xs">
                            {report.profiles.username[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span>{report.profiles.display_name || report.profiles.username}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end">
                    {report.allow_public_sharing && (
                      <Button
                        variant="ghost"
                        size="default"
                        onClick={() => handleShare(report)}
                        className="min-w-[40px] w-10 h-9 p-0 flex items-center justify-center"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {report.status === 'open' && user && report.user_id !== user.id && (
                      <Button
                        variant="outline"
                        size="default"
                        onClick={() => handleJoinHelp(report.id)}
                        disabled={joiningHelper === report.id}
                        className="flex items-center gap-1 min-w-[90px] max-w-[120px] h-9 px-3 text-xs"
                      >
                        <Heart className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {joiningHelper === report.id ? 'Unendo...' : 'Mi unisco'}
                        </span>
                      </Button>
                    )}
                    
                    <Button 
                      variant="outline" 
                      size="default" 
                      onClick={() => onView(report)}
                      className="min-w-[40px] w-10 h-9 p-0 flex items-center justify-center"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};